import { Component, OnInit } from '@angular/core';
import { AuthProvider, AuthProviderForm, SessionsService } from '../../services/sessions.service';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ListboxModule } from 'primeng/listbox';
import { Checkbox } from 'primeng/checkbox';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { CardModule } from 'primeng/card';
import { firstValueFrom } from 'rxjs';
import { MessageModule } from 'primeng/message';

/**
 * Enum representing possible session sign-in flags.
 */
export enum SessionSignInFlag {
  /**
   * Flag indicating that sign-in is required.
   */
  SignInRequired = 'signin-required',
  /**
   * Flag indicating that a session is required.
   */
  SessionRequired = 'session-required',
}

/**
 * Component for handling session sign-in functionality.
 */
@Component({
  selector: 'app-besharp-session-signin',
  imports: [
    Button,
    Checkbox,
    ReactiveFormsModule,
    DividerModule,
    FloatLabelModule,
    InputTextModule,
    ListboxModule,
    InputGroupModule,
    InputGroupAddonModule,
    CardModule,
    FormsModule,
    RouterModule,
    MessageModule,
  ],
  providers: [],
  templateUrl: './session-signin.component.html',
  styleUrls: ['./session-signin.component.scss'], // corrected styleUrl to styleUrls
})
export class SessionSigninComponent implements OnInit {
  /**
   * Enum for session sign-in flags.
   */
  protected eSessionSignInFlag = SessionSignInFlag;

  /**
   * List of available authentication providers.
   */
  protected authProviders: AuthProvider[] = [];

  /**
   * Currently selected authentication provider.
   */
  protected selectedAuthProvider: AuthProvider | null = null;

  /**
   * Form group for sign-in form.
   */
  protected signInFormGroup: FormGroup = new FormGroup({});

  /**
   * Sign-in form data.
   */
  protected signInForm: AuthProviderForm | null = null;

  /**
   * Flag indicating whether sign-in is in progress.
   */
  protected signingIn: boolean = false;

  /**
   * Error message to display.
   */
  protected errorMessage: string | null = null;

  /**
   * Mapping of session sign-in flags to their corresponding labels.
   */
  protected flagLabelMapping = {
    [SessionSignInFlag.SessionRequired]: 'No active session, please sign-in.',
    [SessionSignInFlag.SignInRequired]: 'Session expired, please sign-in again.',
  };

  /**
   * Form group for handling form data.
   */
  protected formGroup: FormGroup = new FormGroup({
    name: new FormControl(''),
  });

  /**
   * Flag indicating whether data is being loaded.
   */
  protected loading: boolean = false;

  /**
   * Flag indicating whether credentials should be saved in the configured storage.
   */
  protected saveCredentials: boolean = false;

  /**
   * List of session sign-in flags.
   */
  protected flags: SessionSignInFlag[] = [];

  /**
   * Constructor for the component.
   *
   * @param sessionsService Service for handling sessions.
   * @param router Router for navigating between routes.
   * @param route Activated route for accessing query parameters.
   */
  public constructor(
    protected sessionsService: SessionsService,
    protected router: Router,
    protected route: ActivatedRoute
  ) {}

  /**
   * Initializes the component.
   */
  async ngOnInit(): Promise<void> {
    this.authProviders = this.sessionsService.getAuthenticationProviders();

    const queryParams = await firstValueFrom(this.route.queryParamMap);
    this.flags = Object.values(SessionSignInFlag).filter((flag) =>
      queryParams.get('flags')?.split(/(,|;)/).includes(flag)
    );
    let sessionToPrefill = queryParams.get('prefill_session');

    if (sessionToPrefill) {
      const session = this.sessionsService.getSession(sessionToPrefill);
      const authProvider = this.sessionsService.getAuthProvider(session.authProvider);
      this.selectAuthProvider(authProvider);

      //Fill the non-sensitive data into the form
      if (session.prefillInfo) {
        for (const param of authProvider.getSignInFields()) {
          if (param.key in session.prefillInfo) {
            this.formGroup.controls[param.key].setValue(session.prefillInfo[param.key]);
          }
        }

        for (const [key, value] of Object.entries(session.authCredentials ?? {})) {
          this.formGroup.controls[key].setValue(value);
        }
      }

      this.formGroup.updateValueAndValidity();
    }
  }

  /**
   * Selects an authentication provider.
   *
   * @param authProvider Authentication provider to select.
   */
  public selectAuthProvider(authProvider: AuthProvider) {
    this.selectedAuthProvider = authProvider;
    this.errorMessage = null;

    const formControls: { [key: string]: FormControl } = {};

    for (const name of Object.keys(this.formGroup.controls)) {
      this.formGroup.removeControl(name);
    }

    for (const control of authProvider.getSignInFields()) {
      switch (control.type) {
        case 'text':
          formControls[control.key] = new FormControl<string>('', Validators.required);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
        case 'email':
          formControls[control.key] = new FormControl<string>('', [Validators.email, Validators.required]);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
        case 'checkbox':
          formControls[control.key] = new FormControl<boolean>(false);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
        case 'password':
          formControls[control.key] = new FormControl<string>('', Validators.required);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
      }
    }

    this.formGroup.updateValueAndValidity();

    this.signInForm = authProvider.getSignInFields();
  }

  /**
   * Checks if the form is valid.
   *
   * @returns True if the form is valid, false otherwise.
   */
  protected isValid(): boolean {
    return this.formGroup.valid;
  }

  /**
   * Handles changes to the save credentials checkbox.
   *
   * @param checked Whether credentials should be saved.
   */
  protected onChangeSaveCredentials(checked: boolean) {
    this.saveCredentials = checked;
  }

  /**
   * Starts a new session.
   *
   * @param event Event that triggered the session start.
   */
  protected async startSession(event: any) {
    console.debug(event);

    this.errorMessage = null;

    if (!this.selectedAuthProvider) {
      return;
    }

    //TODO: Split the data in plaintext and secret
    const authData = this.formGroup.getRawValue();

    this.loading = true;
    this.sessionsService
      .startSession(this.selectedAuthProvider?.name, authData, {
        saveCredentials: this.saveCredentials,
        setActive: true,
      })
      .then((session) => {
        this.router.navigate(['/']);
      })
      .catch((err) => {
        this.errorMessage = err;
      })
      .finally(() => {
        this.loading = false;
      });
  }
}
